import { useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { useParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { userInformationFields } from './helpers.tsx';
import { MfaStatus } from '@shared/constants/mfa';
import {
  AdminActionItem,
  AdminActionsRow,
  FieldsGrid,
  FieldSlot,
  InformationSection,
  InformationTitle,
  InformationWrapper,
  LoginStatus,
  SectionTitle,
  SummaryGrid,
} from './styles';
import { fetchStaffPage } from '../../../actions';
import { ItemWithLabel } from './components/ItemWithLabel/index.tsx';
import { SelectStatus } from './components/SelectStatus/index.tsx';
import { SelectBusinessUnits } from './components/SelectBusinessUnits/index.tsx';
import { ItemFields } from './types.ts';
import { useTranslation } from 'react-i18next';
import { showToast as showToastHelper } from '../../../helpers';
import { showToast as showToastAction } from '../../../actions';
import ResetPasswordButton from './components/ResetPasswordButton';
import UpdateMfaButton from './components/UpdateMfaButton';
import { EditPhoneNumber } from './components/EditPhoneNumber';
import { isOfficeAdmin } from '@containers/Admin/shared/rolePermissions';
import { EnableTimeTracking } from './components/EnableTimeTracking';
import { ExperimentalFeatures } from './components/ExperimentalFeatures';
import { mentorRoles } from '@shared/constants/roles.ts';
import { ROLES } from '@containers/Admin/shared/roles';
import { EditUserName } from './components/EditUserName';

export const UserInformation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { userData, toastType } = useSelector((state: RootStateOrAny) => state.get('staffPage').toJS()); // FIXME replace to typing store
  const { roles } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS()); // FIXME replace to typing store
  const isAdmin = roles.includes('30') || roles.includes('40') || roles.includes('41');
  const isTechAdmin = roles.includes('40');

  const targetIsSupervisor = Array.isArray(userData?.roles) && userData.roles.includes('30');
  const targetIsTechAdmin = Array.isArray(userData?.roles) && userData.roles.includes(ROLES.TECH_ADMIN);
  const canEditBusinessUnits = isTechAdmin && !targetIsTechAdmin;
  const canChangeStatus = !(isOfficeAdmin(roles) && targetIsSupervisor);
  const isClinicalMentorUser = userData?.roles?.some((roleId: string) => mentorRoles.includes(roleId));

  useEffect(() => {
    // Show toast when toastType changes
    if (toastType) {
      showToastHelper(toastType, t);
      // Clear toastType in Redux after showing
      dispatch(showToastAction(null));
    }
  }, [toastType, dispatch, t]);

  useEffect(() => {
    dispatch(fetchStaffPage(id));
  }, []);

  const renderFields = (fields: Array<ItemFields>, fieldIds: Array<string>, wideFields: Array<string> = []) => fields
    .map((field) => ({ field, order: fieldIds.indexOf(field.id) }))
    .filter(({ order }) => order >= 0)
    .sort((left, right) => left.order - right.order)
    .map(({ field }) => field)
    .map(({ id: fieldId, label, content }) => (
      <FieldSlot key={fieldId} className={`${fieldId} ${wideFields.includes(fieldId) ? 'wide' : ''}`}>
        <ItemWithLabel
          key={['phone', 'userName'].includes(fieldId) ? fieldId : nanoid()}
          label={label}
        >
          {content}
        </ItemWithLabel>
      </FieldSlot>
    ));

  const fields = userData ? userInformationFields(
    userData,
    <SelectStatus canChangeStatus={canChangeStatus} />,
    <SelectBusinessUnits canEditBusinessUnits={canEditBusinessUnits} />,
    isAdmin ? <EditPhoneNumber /> : null,
    isAdmin ? <EditUserName /> : userData.userName || '',
  ) : [];
  const hasMfa = userData?.mfaStatus === MfaStatus.SMS || userData?.mfaStatus === MfaStatus.EMAIL;
  const MfaIcon = hasMfa ? CheckIcon : RemoveCircleOutlineIcon;
  const mfaStatusLabel = userData?.mfaStatus === MfaStatus.SMS
    ? t('Authentication.MFA.sms')
    : userData?.mfaStatus === MfaStatus.EMAIL
      ? t('Authentication.MFA.email')
      : t('Authentication.MFA.inactive');
  const hasPassword = Boolean(userData?.isMigratedToCognito);
  const PasswordIcon = hasPassword ? CheckIcon : RemoveCircleOutlineIcon;
  const passwordStatusTitle = hasPassword
    ? t('Authentication.Password.passwordSet')
    : t('Authentication.MFA.inactive');

  return (
    <>
      <InformationTitle>{t('UI.userInformation' as any)}</InformationTitle>
      <InformationWrapper>
        {userData ? (
          <>
            <SummaryGrid>
              {renderFields(fields, ['email'], ['email'])}
            </SummaryGrid>
            <InformationSection className="profile">
              <SectionTitle>{t('Informative.Sections.profile')}</SectionTitle>
              <FieldsGrid className="profile">
                {renderFields(fields, ['status', 'userName'], ['userName'])}
              </FieldsGrid>
            </InformationSection>
            <InformationSection className="program">
              <SectionTitle>{t('Informative.Sections.program')}</SectionTitle>
              <FieldsGrid className="program">
                {renderFields(fields, ['businessUnits'], ['businessUnits'])}
              </FieldsGrid>
            </InformationSection>
            {isAdmin && (
              <InformationSection className="login">
                <SectionTitle>{t('Informative.Sections.login')}</SectionTitle>
                <FieldsGrid className="login">
                  {renderFields(fields, ['phone'], ['phone'])}
                </FieldsGrid>
                <AdminActionsRow>
                  <AdminActionItem>
                    <ItemWithLabel label={<span>{t('Authentication.Password.password')}</span>}>
                      <LoginStatus className={hasPassword ? 'password-set' : 'not-set'} title={passwordStatusTitle}>
                        <PasswordIcon />
                        {!hasPassword && t('Authentication.MFA.inactive')}
                      </LoginStatus>
                      <ResetPasswordButton userId={userData.id} userEmail={userData.email || userData.userName} />
                    </ItemWithLabel>
                  </AdminActionItem>
                  <AdminActionItem>
                    <ItemWithLabel label={<span>{t('Informative.Labels.mfa')}</span>}>
                      <LoginStatus className={hasMfa ? 'mfa-set' : 'not-set'}>
                        <MfaIcon />
                        {mfaStatusLabel}
                      </LoginStatus>
                      <UpdateMfaButton
                        userId={userData.id}
                        userEmail={userData.email || userData.userName}
                        phone={userData.phone}
                      />
                    </ItemWithLabel>
                  </AdminActionItem>
                </AdminActionsRow>
              </InformationSection>
            )}
            <InformationSection className="advanced">
              <SectionTitle>{t('Informative.Sections.advanced')}</SectionTitle>
              <FieldsGrid className="advanced">
                {isClinicalMentorUser && <EnableTimeTracking />}
                <ExperimentalFeatures />
              </FieldsGrid>
            </InformationSection>
          </>
        ) : (
          t('UI.noInformation' as any)
        )}
      </InformationWrapper>
    </>
  );
};
